import { useState, useEffect } from 'react';
import { ordersService } from '@/shared/services/ordersService';
import { Order, OrderStatus } from '@/shared/types/orders';
import { OrderDetailModal } from '../components/OrderDetailModal';
import styles from './OrdersPage.module.css';

const ORDER_STATUSES: OrderStatus[] = [
    "Pendiente",
    "En preparación",
    "Listo para despacho",
    "Buscando repartidor",
    "Asignado",
    "Recogido",
    "Entregado",
    "Cancelado",
    "Rechazado",
    "Incidencia"
];

export const OrdersPage = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

    // Filters
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const limit = 50;

    useEffect(() => {
        fetchOrders();
    }, [page, statusFilter]);

    const fetchOrders = async (searchTerm?: string) => {
        setLoading(true);
        setError('');
        try {
            // Support filtering by exact status if selected
            const params = {
                limit,
                offset: page * limit,
                status: statusFilter || undefined,
                search: searchTerm !== undefined ? searchTerm : search || undefined
            };

            const response = await ordersService.listOrders(params);

            setOrders(response.orders);
            // Check if there are more pages based on response
            setHasMore(response.pagination.hasMore);

        } catch (err: any) {
            console.error('Error fetching orders:', err);
            setError('Error al cargar la lista de pedidos. Por favor, intenta de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setPage(0);
        fetchOrders(search);
    };

    const handleNextPage = () => {
        if (hasMore) setPage(p => p + 1);
    };

    const handlePrevPage = () => {
        if (page > 0) setPage(p => p - 1);
    };

    const refresh = () => {
        setPage(0); // Reset to first page on manual refresh
        setSearch('');
        fetchOrders('');
    };

    const formatDate = (timestamp?: any) => {
        if (!timestamp) return 'N/A';

        let date: Date;

        if (typeof timestamp === 'string') {
            date = new Date(timestamp);
        } else if (timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else if (timestamp._seconds) {
            date = new Date(timestamp._seconds * 1000);
        } else {
            return 'N/A';
        }

        if (isNaN(date.getTime())) return 'Fecha inválida';

        return date.toLocaleString('es-ES', {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    const getStatusClass = (status: string) => {
        // Normalize status string to match CSS class naming convention
        // e.g., "En preparación" -> "statusEnPreparación"
        // Simply removing spaces might be enough for the CSS classes defined
        return styles[`status${status.replace(/\s+/g, '')}`] || '';
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h1>Monitor de Pedidos</h1>
                        <p>Visualización en tiempo real de todos los pedidos del sistema.</p>
                    </div>
                    <button onClick={refresh} className={styles.refreshButton} disabled={loading}>
                        Actualizar / Limpiar
                    </button>
                </div>
            </div>

            <div className={styles.filters}>
                <div className={styles.filterGroup}>
                    <label>Buscar por Código</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                            type="text"
                            placeholder="Ej: ORD-X9J2K"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <button onClick={handleSearch} className={styles.refreshButton} style={{ padding: '0.625rem' }}>🔍</button>
                    </div>
                </div>

                <div className={styles.filterGroup}>
                    <label>Filtrar por Estado</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(0); // Reset to first page when filter changes
                        }}
                    >
                        <option value="">Todos los estados</option>
                        {ORDER_STATUSES.map(status => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>
                </div>
            </div>

            {error && <div className={styles.error} style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}

            {selectedOrderId && (
                <OrderDetailModal
                    orderId={selectedOrderId}
                    onClose={() => setSelectedOrderId(null)}
                />
            )}

            <div className={styles.tableContainer}>
                {loading && orders.length === 0 ? (
                    <div className={styles.emptyState}>Cargando pedidos...</div>
                ) : orders.length === 0 ? (
                    <div className={styles.emptyState}>No se encontraron pedidos con los filtros seleccionados.</div>
                ) : (
                    <>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Fecha</th>
                                    <th>Estado</th>
                                    <th>Cliente</th>
                                    {/* <th>Restaurante</th> - Available often but let's check if we have Name */}
                                    {/* <th>Repartidor</th> - ID available, name might not be populated in list view */}
                                    <th>Total</th>
                                    <th>Detalles</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map((order) => (
                                    <tr key={order.orderId}>
                                        <td style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                            {order.orderCode}
                                        </td>
                                        <td>{formatDate(order.createdAt)}</td>
                                        <td>
                                            <span className={`${styles.statusBadge} ${getStatusClass(order.status)}`}>
                                                {order.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontWeight: 600 }}>{order.customerName || `ID: ${order.customerId.substring(0, 8)}...`}</span>
                                                <span className={`${styles.typeBadge} ${order.type === 'delivery' ? styles.typeDelivery : styles.typePickup}`}>
                                                    {order.type === 'delivery' ? 'Entrega' : 'Retiro'}
                                                </span>
                                            </div>
                                        </td>
                                        <td>${order.financial?.total?.toFixed(2)}</td>
                                        <td>
                                            <button
                                                className={styles.refreshButton}
                                                style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem' }}
                                                onClick={() => setSelectedOrderId(order.orderId)}
                                            >
                                                Ver Detalle
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className={styles.pagination}>
                            <span className={styles.paginationInfo}>
                                Página {page + 1}
                            </span>
                            <div className={styles.paginationButtons}>
                                <button
                                    className={styles.pageButton}
                                    onClick={handlePrevPage}
                                    disabled={page === 0 || loading}
                                >
                                    Anterior
                                </button>
                                <button
                                    className={styles.pageButton}
                                    onClick={handleNextPage}
                                    disabled={!hasMore || loading}
                                >
                                    Siguiente
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
